/*
 *  * Copyright (c) by INFODATA-GROUP. All rights reserved.
 *  * No part of this publication may be reproduced, distributed,
 *  * or transmitted in any form or by any means, including photocopying, recording,
 *  * or other electronic or mechanical methods, without the prior written permission of the publisher,
 *  * except in the case of brief quotations embodied in critical
 *  * reviews and certain other noncommercial uses permitted by copyright law.
 *  *  author : BS
 *  *  date : 28/09/2022 17:54
 */

import {
  HttpClient,
  HttpErrorResponse,
  HttpResponse,
} from '@angular/common/http';
import { EventEmitter, inject, Injectable } from '@angular/core';
import { defer, Observable, of, throwError } from 'rxjs';
import {
  catchError,
  delay,
  expand,
  filter,
  finalize,
  map,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { NoteBuilder } from '../../builder/note.builder';
import { ProgressStatus } from '../../enum/progress-status.enum';
import { Style } from '../../enum/style.enum';
import { QueueJobResponseDto } from '../../interface/queue-job-response.dto';
import { RestSettings } from '../../interface/rest-settings';
import { DocumentResponse } from '../../model/document-response';
import { GedFile } from '../../model/ged-file';
import { Note } from '../../model/notification/note';
import { RestError } from '../../model/rest-error';
import { CookieService } from '../../service/cookie.service';
import { LoaderService } from '../../service/loader.service';
import { NotificationService } from '../../service/notification.service';
import { NameValue } from '../../tool/name-value';
import { AppConfig } from '../app-config';
import { DataModel } from '../data-model';
import { DataModelConverter } from '../data-model-converter';
import { Factory } from './factory/factory';
import { HttpFilter } from './http-filter';

const withLoader = (loader: LoaderService, settings?: RestSettings) => {
  const showLoader = !settings || settings.showLoading !== false;
  return (source: Observable<any>) =>
    defer(() => {
      if (showLoader) {
        loader.startLoad();
      }
      return source;
    }).pipe(
      finalize(() => {
        if (showLoader) {
          loader.stopLoad();
        }
      }),
    );
};

@Injectable({
  providedIn: 'root',
})
export class RestService {
  public restErrorE = new EventEmitter<RestError>();
  public httpClient = inject(HttpClient);
  public config = inject(AppConfig);
  protected loader = inject(LoaderService);
  private cookieService = inject(CookieService);

  public shouldUpdateCookies = true;

  protected notificationService = inject(NotificationService);

  protected queueJobsIndex = 1;

  private updateCookies(): void {
    if (!this.shouldUpdateCookies) {
      return;
    }
    this.cookieService.refresh(
      ['witoken', 'userId', 'userName', 'connectionMode'],
      document.domain,
    );
  }

  public get<T>(factory: Factory<T>, settings?: RestSettings): Observable<T> {
    const url = this.getUrl(factory, settings?.httpFilter);
    return this.httpClient.get<unknown[]>(url).pipe(
      withLoader(this.loader, settings),
      map((data) => {
        // No data or wrong array
        if (!data || (data instanceof Array && data.length !== 1)) {
          return null;
        }
        if (data instanceof Array) {
          return factory.build(data[0]);
        }
        return factory.build(data);
      }),
      tap(() => {
        this.updateCookies();
      }),
      catchError((error) => {
        return this.catchError(error, 'get', settings?.isSilent);
      }),
    );
  }

  public getAll<T>(
    factory: Factory<T>,
    settings?: RestSettings,
  ): Observable<T[]> {
    const url = this.getUrl(factory, settings?.httpFilter);
    return this.getByHttpRequest(factory, url, 'getAll', settings).pipe(
      tap(() => {
        this.updateCookies();
      }),
    );
  }

  public getWithHeader<T>(
    factory: Factory<T>,
    settings?: RestSettings,
  ): Observable<{ headers: any; data: T }> {
    const url = this.getUrl(factory, settings?.httpFilter);
    return this.httpClient.get<unknown[]>(url, { observe: 'response' }).pipe(
      withLoader(this.loader, settings),
      map((data) => {
        // No data or wrong array
        if (
          !data.body ||
          (data.body instanceof Array && data.body.length !== 1)
        ) {
          return null;
        }
        if (data.body instanceof Array) {
          return { headers: data.headers, data: factory.build(data.body[0]) };
        }
        return { headers: data.headers, data: factory.build(data.body) };
      }),
      catchError((error) => {
        return this.catchError(error, 'get', settings?.isSilent);
      }),
    );
  }

  /**
   * Get a list of data on the server with requestHeader (Based On WI-RFL Protocol)
   */
  public getAllWithHeader<T>(
    factory: Factory<T>,
    settings?: RestSettings,
  ): Observable<{ headers: any; data: T[] }> {
    const url = this.getUrl(factory, settings?.httpFilter);
    return this.httpClient.get<any[]>(url, { observe: 'response' }).pipe(
      withLoader(this.loader, settings),
      map((data) => {
        return { headers: data.headers, data: this.map(factory, data.body) };
      }),
      catchError((error) => {
        return this.catchError(error, 'get', settings?.isSilent);
      }),
    );
  }

  public getMock<T>(
    factory: Factory<T>,
    settings?: RestSettings,
  ): Observable<T[]> {
    return this.getByHttpRequest(
      factory,
      factory.getApi(),
      'getMock',
      settings,
    );
  }

  public getByHttpRequest<T>(
    factory: Factory<T>,
    url: string,
    source: string,
    settings?: RestSettings,
  ): Observable<T[]> {
    return this.httpClient
      .get<unknown[]>(url)
      .pipe(withLoader(this.loader, settings))
      .pipe(
        map((data) => {
          return this.map(factory, data);
        }),
        catchError((error) => {
          return this.catchError(error, source, settings?.isSilent);
        }),
      );
  }

  /**
   * Get an entity by id
   */
  public getById<T>(
    factory: Factory<T>,
    id: string,
    settings?: RestSettings,
  ): Observable<T> {
    const url = this.getUrl(factory, settings?.httpFilter, id);
    return this.httpClient.get<any[]>(url).pipe(
      withLoader(this.loader, settings),
      map((data) => {
        if (data instanceof Array) {
          if (data.length === 1) {
            return factory.build(data[0]);
          } else {
            return null;
          }
        } else if (data) {
          return factory.build(data);
        } else {
          return null;
        }
      }),
      tap(() => {
        this.updateCookies();
      }),
      catchError((error) => {
        return this.catchError(error, 'get', settings?.isSilent);
      }),
    );
  }

  /**
   * Return a list of element corresponding to the searchQuery (// TODO Link of supersearch)
   * @param factory
   * @param url
   * @param searchQuery
   */
  public search<T>(
    factory: Factory<T>,
    url: string,
    searchQuery: string,
    settings?: RestSettings,
  ): Observable<T[]> {
    if (!settings?.httpFilter) {
      if (!settings) settings = {} as RestSettings;
      settings.httpFilter = new HttpFilter();
    }
    settings.httpFilter.setFilters('QUERY', searchQuery);
    return this.httpClient
      .get<any>(this.buildUrl(url, settings.httpFilter))
      .pipe(
        withLoader(this.loader, settings),
        map((result) => {
          if (result && result instanceof Array) {
            return result.map((d) => factory.build(d));
          }
          if (result && Object.hasOwn(result, 'MMX')) {
            return result.MMX.map((d) => factory.build(d));
          }
          return null;
        }),
        tap(() => {
          this.updateCookies();
        }),
      );
  }

  /**
   * Get list of Entity on the server (Based On Wi-RFL Protocol and get x-wi-total-count parameters
   * @param factory
   * @param settings
   */
  public getAllWithCount<T>(
    factory: Factory<T>,
    settings?: RestSettings,
  ): Observable<{ count: number; data: T[] }> {
    const url = this.getUrl(factory, settings?.httpFilter);
    return this.httpClient.get<unknown[]>(url, { observe: 'response' }).pipe(
      withLoader(this.loader, settings),
      map((result) => {
        const count = parseInt(result.headers.get('x-wirfl-totalcount'), 10);
        const data = result.body.map((item) => {
          return factory.build(item);
        });
        return { count: count, data: data };
      }),
      tap(() => {
        this.updateCookies();
      }),
      catchError((error) => {
        return this.catchError(error, 'getWithCount', settings?.isSilent);
      }),
    );
  }

  /**
   * Post a classic data (!= Entity) on the server.
   */
  public post(
    api: string,
    data: any,
    settings?: RestSettings,
  ): Observable<any> {
    return this.httpClient
      .post<any[]>(this.buildUrl(api, settings?.httpFilter), data)
      .pipe(
        withLoader(this.loader, settings),
        tap(() => {
          this.updateCookies();
        }),
        catchError((error) => {
          return this.catchError(error, 'post', settings?.isSilent);
        }),
      );
  }

  /**
   * Put a data to the server
   * @param api api on the server
   * @param data to update
   * @param isSilent true to not display notification if error is caught
   */
  public put(api: string, data: any, settings?: RestSettings): Observable<any> {
    return this.httpClient
      .put<any[]>(this.buildUrl(api, settings?.httpFilter), data)
      .pipe(
        withLoader(this.loader, settings),
        tap(() => {
          this.updateCookies();
        }),
        catchError((error) => {
          return this.catchError(error, 'put', settings?.isSilent);
        }),
      );
  }

  /**
   * Delete an Entity on the server
   * @param api
   * @param id
   * @param settings
   */
  public delete(
    api: string,
    id?: string,
    settings?: RestSettings,
  ): Observable<any> {
    const url = this.buildUrl(api, settings?.httpFilter, id);
    return this.httpClient.delete(url).pipe(
      withLoader(this.loader, settings),
      tap(() => {
        this.updateCookies();
      }),
      catchError((error) => {
        return this.catchError(error, 'delete', settings?.isSilent);
      }),
    );
  }

  public getSchema(
    api: string,
    settings?: RestSettings,
  ): Observable<DataModel> {
    return this.getModelByHttpRequest(api, settings);
  }

  private getModelByHttpRequest(
    url: string,
    settings?: RestSettings,
  ): Observable<DataModel> {
    return this.httpClient.get(url).pipe(
      withLoader(this.loader, settings),
      map((data) => {
        return new DataModelConverter().convert(data, null);
      }),
      catchError((error) => {
        return this.catchError(
          error,
          'getModelByHttpRequest',
          settings?.isSilent,
        );
      }),
    );
  }

  /**
   * Get Rfl Schema on the server (Based on WI-RFL Protocol)
   * @param factory
   */
  public options(api: string, settings?: RestSettings): Observable<DataModel> {
    if (this.config.dataModelMethod === '$metadata') {
      const metadata = api.endsWith('/') ? '$metadata' : '/$metadata';
      const url = this.buildUrl(api + metadata, settings?.httpFilter);
      return this.getModelByHttpRequest(url, settings);
    }
    const url = this.buildUrl(api, settings?.httpFilter);
    return this.httpClient.options(url).pipe(
      withLoader(this.loader, settings),
      map((data) => {
        return new DataModelConverter().convert(data, null);
      }),
      tap(() => {
        this.updateCookies();
      }),
      catchError((error) => {
        return this.catchError(error, 'options', settings?.isSilent);
      }),
    );
  }

  /**
   * Convert Entity to complete request url
   * @param factory {@see Factory}
   * @param filter {@see HttpFilter}
   * @param id : unique id of record
   */
  public getUrl(
    factory: Factory<any>,
    filter?: HttpFilter,
    id?: string,
  ): string {
    return this.buildUrl(factory.getApi(), filter, id);
  }

  private buildUrl(url: string, filter?: HttpFilter, id?: string): string {
    let base = url;
    if (this.config && this.config.apiEndPoint) {
      base = this.config.apiEndPoint + url;
    }

    if (url.startsWith('http') || url.startsWith('https')) {
      base = url;
    }

    if (id) {
      base += base.charAt(base.length - 1) === '/' ? id : `/${id}`;
    }
    if (filter) {
      if (base.indexOf('?') < 0) {
        base += '?';
      }
      base += filter.toString();
    }
    if (base.charAt(base.length - 1) === '?') {
      base = base.slice(0, base.length - 1);
    }
    return base;
  }

  /**
   * Send On file to the server (Based on WI Ged Prootocol)
   * @param url : Api on the server
   * @param file : File to Send ({@see GedFile})
   * @param settings : {@see RestSettings}
   */
  public sendFile(
    url: string,
    file: GedFile,
    settings?: RestSettings,
  ): Observable<boolean> {
    const eventEmitter = new EventEmitter<boolean>();
    const fileReader = new FileReader();
    fileReader.readAsDataURL(file.getFile());
    fileReader.onload = () => {
      this.sendEncodedFileToServer(
        url,
        file.name,
        <string>fileReader.result,
        file.dds,
        settings,
      ).subscribe({
        next: (result: any) => {
          if (Object.hasOwn(result, 'Code')) {
            file.metadata = result.Code;
            file.status = ProgressStatus.send;
            eventEmitter.emit(true);
          } else {
            file.status = ProgressStatus.error;
            eventEmitter.emit(false);
          }
        },
        error: () => {
          file.status = ProgressStatus.error;
          eventEmitter.emit(false);
        },
      });
    };
    return eventEmitter;
  }

  public sendEncodedFileToServer(
    url,
    name: string,
    fileEncoded: string,
    dds?: NameValue[],
    settings?: RestSettings,
  ): Observable<any> {
    const formData = new FormData();
    formData.append('FILENAME', name);
    formData.append('CONTENT', fileEncoded.split(',')[1]);
    if (dds) {
      const serial = [];
      for (const nv of dds) {
        serial.push(`${nv.name}|${nv.value}`);
      }
      formData.append('DDF', serial.join(' '));
    }
    return this.httpClient
      .post(url, formData)
      .pipe(withLoader(this.loader, settings));
  }

  /**
   * Remove the file on the server
   * @param url : end point api
   * @param metadata : meta key of the ged file
   * @param settings
   */
  public removeFile(
    url: string,
    metadata: string,
    settings?: RestSettings,
  ): Observable<any> {
    url = this.buildUrl(url, settings?.httpFilter, metadata);
    return this.httpClient.delete(url).pipe(withLoader(this.loader, settings));
  }

  /**
   * @param error
   * @param source
   * @param isSilent true to not display notification if error is catched
   */
  public catchError(
    error: HttpErrorResponse,
    source: string,
    isSilent: boolean,
  ): Observable<any> {
    const returnError = new RestError(error);
    returnError.source = source;

    if (!isSilent && error.status !== 401 && error.status !== 408) {
      this.restErrorE.emit(returnError);
    }
    return throwError(returnError);
  }

  private map<T>(factory: Factory<T>, content: any | any[]): T[] {
    if (content instanceof Array) {
      return content.map((item) => {
        return factory.build(item);
      });
    }
    return null;
  }

  /**
   * Get a document or a json.
   * Check type of the response and return a DocumentResponse with object property a blob or a JSON
   * @param url
   * @param data
   * @param settings
   */
  public getDocument(
    url: string,
    data?: any,
    settings?: RestSettings,
  ): Observable<DocumentResponse> {
    return this.httpClient
      .post(url, data, { responseType: 'blob', observe: 'response' })
      .pipe(
        withLoader(this.loader, settings),
        switchMap((response) => {
          if (response.status === 200) {
            return of(new DocumentResponse('pdf', response.body));
          } else if (response.status === 201) {
            const fileReader = new FileReader();
            const observable = new Observable<DocumentResponse>((observer) => {
              fileReader.onload = function (event) {
                observer.next(
                  new DocumentResponse(
                    'json',
                    JSON.parse((event.currentTarget as any).result),
                  ),
                );
              };
            });
            fileReader.readAsText(response.body);
            return observable;
          }
          return undefined;
        }),
        catchError((error) => {
          return this.catchError(error, 'post', settings?.isSilent);
        }),
      );
  }

  /**
   * @Deprecated:: Handle by function withLoader
   * @param settings
   */
  /* istanbul ignore next */
  public finalize(settings: RestSettings) {
    if (this.checkShowLoader(settings)) {
      this.loader.stopLoad();
    }
  }

  /**
   * @Deprecated:: Handle by function withLoader
   * @param settings
   */
  /* istanbul ignore next */
  public checkShowLoader(settings): boolean {
    return !(
      settings &&
      Object.hasOwn(settings, 'showLoading') &&
      !settings.showLoading
    );
  }

  /**
   * POST on an api which may answer directly or with a queue job.
   * - The filter JOB_QUEUES=1 is added: the front accepts a queue job, the server decides.
   * - 200: direct response, its body is the result.
   * - 202 with a {@see QueueJobResponseDto} body: queue job. A notification shows its progress,
   *   data.links.self is polled every Retry-After seconds (default 10) and the job ends when the
   *   polling is redirected to another url: the body of that response is the result.
   * - Errors are thrown as a {@see RestError}. Before the job starts: same handling as post
   *   (restErrorE, unless settings.isSilent). Once the job has started: shown by its notification only.
   *
   * Planned: native queue job support in post, this method will then be deprecated in favour of post.
   * @param api
   * @param data : body of the request
   * @param settings
   */
  public postJob<T = any>(
    api: string,
    data: any,
    settings?: RestSettings,
  ): Observable<T> {
    const httpFilter = this.addQueueJobFilter(settings?.httpFilter);
    const url = this.buildUrl(api, httpFilter);

    let queueJobUrl = '';
    let endQueueJob = false;
    const index = this.queueJobsIndex;
    const notification = new NoteBuilder('i18n.common.queueJob.titleRunning')
      .setContent('i18n.common.queueJob.messageRunning')
      .setSpinnerNote()
      .setIndex(index)
      .build();
    this.queueJobsIndex++;

    return this.httpClient.post<any>(url, data, { observe: 'response' }).pipe(
      expand((response) => {
        // Direct response
        if (response.status === 200 && !queueJobUrl) {
          return of(response);
        }
        // QueueJob loop
        /* istanbul ignore else */
        if (response.status === 200 && queueJobUrl) {
          /* istanbul ignore else */
          if (!response.url.includes(queueJobUrl)) {
            endQueueJob = true;
            notification.title = 'i18n.common.queueJob.titleEnded';
            if (
              notification.content === 'i18n.common.queueJob.messageRunning'
            ) {
              notification.content = 'i18n.common.queueJob.messageEnded';
            }
            notification.canClose = true;
            notification.isLoading = false;
            notification.progress = 100;
            return of(response);
          }
          return this.getNextRequestQueueJob(response, notification);
        }
        // Detecting QueueJob
        /* istanbul ignore else */
        if (response.status === 202 && this.isBodyQueueJob(response.body)) {
          queueJobUrl = this.buildUrl(response.body.data.links.self);
          const retryAfter =
            response.headers.get('Retry-After') ||
            /* istanbul ignore next */ '10';
          this.notificationService.addNotification(notification);
          return of(null).pipe(
            delay(parseInt(retryAfter, 10) * 1000),
            switchMap(() =>
              this.getNextRequestQueueJob(response, notification),
            ),
          );
        }
        return of(response);
      }),
      filter(
        (response) => (response.status === 200 && !queueJobUrl) || endQueueJob,
      ),
      switchMap((response: HttpResponse<T>) => {
        return of(response.body);
      }),
      take(1),
      finalize(() => this.finalize(settings)),
      catchError((error) =>
        this.catchQueueJobError(
          error,
          'post',
          !!queueJobUrl,
          notification,
          settings,
        ),
      ),
    );
  }

  /**
   * PUT on an api which may answer directly or with a queue job.
   * - The filter JOB_QUEUES=1 is added: the front accepts a queue job, the server decides.
   * - 200: direct response, its body is the result.
   * - 202 with a {@see QueueJobResponseDto} body: queue job. A notification shows its progress,
   *   data.links.self is polled every Retry-After seconds (default 10) and the job ends when the
   *   polling is redirected to another url: the body of that response is the result.
   * - Errors are thrown as a {@see RestError}. Before the job starts: same handling as put
   *   (restErrorE, unless settings.isSilent). Once the job has started: shown by its notification only.
   *
   * Planned: native queue job support in put, this method will then be deprecated in favour of put.
   * @param api
   * @param data : body of the request
   * @param settings
   */
  public putJob<T = any>(
    api: string,
    data: any,
    settings?: RestSettings,
  ): Observable<T> {
    const httpFilter = this.addQueueJobFilter(settings?.httpFilter);
    const url = this.buildUrl(api, httpFilter);

    let queueJobUrl = '';
    let endQueueJob = false;
    const index = this.queueJobsIndex;
    const notification = new NoteBuilder('i18n.common.queueJob.titleRunning')
      .setContent('i18n.common.queueJob.messageRunning')
      .setSpinnerNote()
      .setIndex(index)
      .build();
    this.queueJobsIndex++;

    return this.httpClient.put<any>(url, data, { observe: 'response' }).pipe(
      expand((response) => {
        // Direct response
        if (response.status === 200 && !queueJobUrl) {
          return of(response);
        }
        // QueueJob loop
        /* istanbul ignore else */
        if (response.status === 200 && queueJobUrl) {
          /* istanbul ignore else */
          if (!response.url.includes(queueJobUrl)) {
            endQueueJob = true;
            notification.title = 'i18n.common.queueJob.titleEnded';
            if (
              notification.content === 'i18n.common.queueJob.messageRunning'
            ) {
              notification.content = 'i18n.common.queueJob.messageEnded';
            }
            notification.canClose = true;
            notification.isLoading = false;
            notification.progress = 100;
            return of(response);
          }
          return this.getNextRequestQueueJob(response, notification);
        }
        // Detecting QueueJob
        /* istanbul ignore else */
        if (response.status === 202 && this.isBodyQueueJob(response.body)) {
          queueJobUrl = this.buildUrl(response.body.data.links.self);
          const retryAfter =
            response.headers.get('Retry-After') ||
            /* istanbul ignore next */ '10';
          this.notificationService.addNotification(notification);
          return of(null).pipe(
            delay(parseInt(retryAfter, 10) * 1000),
            switchMap(() =>
              this.getNextRequestQueueJob(response, notification),
            ),
          );
        }
        return of(response);
      }),
      filter(
        (response) => (response.status === 200 && !queueJobUrl) || endQueueJob,
      ),
      switchMap((response: HttpResponse<T>) => {
        return of(response.body);
      }),
      take(1),
      catchError((error) =>
        this.catchQueueJobError(
          error,
          'put',
          !!queueJobUrl,
          notification,
          settings,
        ),
      ),
    );
  }

  /**
   * Error of postJob / putJob
   * @param error
   * @param source : 'post' or 'put'
   * @param jobStarted : true when the server answered with a queue job
   * @param notification : progress notification of the job
   * @param settings
   */
  private catchQueueJobError(
    error: HttpErrorResponse,
    source: 'post' | 'put',
    jobStarted: boolean,
    notification: Note,
    settings?: RestSettings,
  ): Observable<any> {
    if (!jobStarted) {
      // No job started: same handling as post / put
      return this.catchError(error, source, settings?.isSilent);
    }
    // Job started: its notification shows the error, restErrorE is not emitted to avoid a double display
    const returnError = new RestError(error);
    notification.title = 'i18n.common.queueJob.titleEndedError';
    notification.content =
      returnError.message ||
      /* istanbul ignore next */ 'i18n.common.queueJob.messageEndedError';
    notification.progress = 100;
    notification.info = returnError.toString();
    notification.isLoading = false;
    notification.style = Style.danger;
    notification.canClose = true;
    return throwError(() => returnError);
  }

  private getNextRequestQueueJob(
    response: HttpResponse<QueueJobResponseDto>,
    note: Note,
  ): Observable<any> {
    const retryAfter =
      response.headers.get('Retry-After') || /* istanbul ignore next */ '10';
    const queueJobUrl = this.buildUrl(response.body.data.links.self);
    return this.httpClient.get<any>(queueJobUrl, { observe: 'response' }).pipe(
      tap((response) => {
        this.handleQueueJobResponse(response.body, note);
      }),
      delay(parseInt(retryAfter, 10) * 1000),
    );
  }
  private isBodyQueueJob(body: any): body is QueueJobResponseDto {
    if (!body) {
      return false;
    }
    if (!body.data) {
      return false;
    }
    if (body.data.type !== 'queue-jobs') {
      return false;
    }
    return true;
  }
  private addQueueJobFilter(filter?: HttpFilter): HttpFilter {
    if (!filter) {
      return new HttpFilter([new NameValue('JOB_QUEUES', '1')]);
    }
    if (!filter.getFilter('JOB_QUEUES')) {
      filter.setFilters('JOB_QUEUES', '1');
    }
    return filter;
  }
  private handleQueueJobResponse(dto: QueueJobResponseDto, notification: Note) {
    if (!this.isBodyQueueJob(dto)) {
      return;
    }
    const data = dto.data.attributes;
    if (data.percent !== undefined) {
      notification.progress = data.percent;
    }
    if (data.message) {
      notification.content = data.message;
    }
  }
}
